import { useMemo, useState, useCallback } from "react";
import { Alert, Box, LinearProgress, Paper, Stack, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { PostAdd } from "@mui/icons-material";

type UploadState = {
  total: number;
  currentIndex: number; // 0-based
  currentFileProgress: number; // 0..1
  currentFileName: string;
};

type ImageUploadPickerProps = {
  onUploadMultiple: (items: any[]) => void;
  apiService: any;
  /** If true, allow non-image files as well. If false/omitted, only images are accepted. */
  allowFiles?: boolean;

  /** If true, allow multiple selection/drop. */
  allowMultiple?: boolean;
};

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
};

const focusedStyle = {
  backgroundColor: "#eaeaea",
};

const acceptStyle = {
  backgroundColor: "rgb(223, 255, 205)",
};

const rejectStyle = {
  backgroundColor: "rgb(255, 205, 205)",
};

export function ImageUploadPicker({ onUploadMultiple, allowFiles, allowMultiple, apiService }: ImageUploadPickerProps) {
  const [error, setError] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState | null>(null);

  const isUploading = !!uploadState;

  const uploadFiles = useCallback(
    async (files: File[]) => {
      setError(null);

      const results: any[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        setUploadState({
          total: files.length,
          currentIndex: i,
          currentFileProgress: 0,
          currentFileName: file.name,
        });

        try {
          const base64 = await fileToBase64(file);

          const formData = new FormData();
          formData.append("b64file", base64);
          formData.append("b64name", file.name);

          // In your existing uploader you always set thumbnail=true.
          // If you allow non-image files, it’s safer to only request thumbnails for images.
          const isImage = file.type?.startsWith("image/") ?? false;
          formData.append("thumbnail", isImage ? "true" : "false");

          const uploadResult = await apiService.uploadFileApp({
            onProgress: (fraction: number) => {
              setUploadState({
                total: files.length,
                currentIndex: i,
                currentFileProgress: fraction,
                currentFileName: file.name,
              });
            },
          })(formData);

          // Pass through the server response, but include useful local metadata
          // (won’t break consumers that only care about uploadResult.file/fileName/etc.)
          results.push({
            ...uploadResult,
            originalFilename: file.name,
            mimeType: file.type || "application/octet-stream",
            size: file.size,
            lastModified: file.lastModified,
          });

          // Ensure we visually hit 100% for this file before moving on
          setUploadState({
            total: files.length,
            currentIndex: i,
            currentFileProgress: 1,
            currentFileName: file.name,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : "Upload fehlgeschlagen";
          setError(msg);
          setUploadState(null);
          return; // abort remaining uploads (matches typical UX)
        }
      }

      setUploadState(null);
      onUploadMultiple(results);
    },
    [onUploadMultiple],
  );

  const { getRootProps, getInputProps, isDragActive, isFocused, isDragAccept, isDragReject } = useDropzone({
    disabled: isUploading,
    multiple: !!allowMultiple,
    accept: allowFiles ? undefined : { "image/*": [] },
    onDrop: (acceptedFiles) => {
      // If allowFiles is false, dropzone already filters to images via accept,
      // but we defensively filter again to avoid surprises.
      const filtered = allowFiles ? acceptedFiles : acceptedFiles.filter((f) => f.type.startsWith("image/"));
      if (filtered.length === 0) return;

      const chosen = allowMultiple ? filtered : [filtered[0]];
      void uploadFiles(chosen);
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  );

  return (
    <Stack spacing={1}>
      <Paper variant="outlined" {...getRootProps({ style })}>
        <input
          {...getInputProps({
            // Helpful for the file picker dialog in some browsers
            ...(allowFiles ? {} : { accept: "image/*" }),
          })}
        />
        <Box sx={{ textAlign: "center", color: "#444" }}>
          <PostAdd />
          <Box>{allowFiles ? "Dateien hier ablegen" : "Fotos hier ablegen"}</Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {isUploading
              ? "Upload läuft…"
              : isDragActive
                ? "Loslassen zum Hochladen"
                : allowMultiple
                  ? "Drag & Drop oder klicken (mehrere möglich)"
                  : "Drag & Drop oder klicken"}
          </Typography>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      {uploadState && (
        <Stack spacing={0.5}>
          <Typography variant="body2">
            Upload {uploadState.currentIndex + 1} / {uploadState.total}: {uploadState.currentFileName}
          </Typography>
          <LinearProgress variant="determinate" value={uploadState.currentFileProgress * 100} />
        </Stack>
      )}
    </Stack>
  );
}

async function fileToBase64(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
